/**
 * @author       justin.jeong@zumper.com (Justin Jeong)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  currentSearch actions
 */

import type { CityGuideCity } from '@zcode/bizos'
import type {
  CityGuideNeighborhood,
  Listable,
  Pin,
  Property,
  RawPinInventorySearch,
} from '@zcode/state-type'
import type { MaybeEmpty } from '@zcode/utility-types'
import { createAction } from 'redux-actions'
import {
  CLEAR_CITY_GUIDE_CITY_DATA,
  CLEAR_CITY_GUIDE_NEIGHBORHOOD_DATA,
  ERROR_FETCHING_CITY_GUIDE_CITY_DATA,
  ERROR_FETCHING_CITY_GUIDE_NEIGHBORHOOD_DATA,
  HAS_MORE_LISTABLES,
  NO_MORE_LISTABLES,
  RECEIVE_CATEGORY_LISTABLES,
  RECEIVE_CITY_GUIDE_CITY_DATA,
  RECEIVE_CITY_GUIDE_NEIGHBORHOOD_DATA,
  RECEIVE_PAGINATED_IDS,
  SET_FIRST_PAGE_ADDITIONAL_COUNT,
  SET_FIRST_PAGE_COUNT,
  SET_FIRST_PAGE_OFFSET,
  SET_FROM_PAID_MARKETING,
  SET_HOOD_VERTICALS,
  SET_IS_CATEGORIES_LOADING,
  SET_IS_NAVBAR_EXPANDED,
  SET_LAST_CLICKED_ENTITY,
  SET_ROUTE_PATHNAME,
  SET_ROUTE_SEARCHSTRING,
  START_FETCHING_CITY_GUIDE_CITY_DATA,
  START_FETCHING_CITY_GUIDE_NEIGHBORHOOD_DATA,
  UPDATE_ADDITIONAL_BUILDING_DATA,
} from '../constants/index'

export const hasMoreListables = createAction(HAS_MORE_LISTABLES)
export const noMoreListables = createAction(NO_MORE_LISTABLES)
export const updateAdditionalBuildingData = createAction(UPDATE_ADDITIONAL_BUILDING_DATA)
export const setFromPaidMarketing = createAction<boolean, typeof SET_FROM_PAID_MARKETING>(
  SET_FROM_PAID_MARKETING
)
export const receiveCategoryListables = createAction(RECEIVE_CATEGORY_LISTABLES)
export const receivePaginatedIds = createAction<
  readonly number[] | null,
  typeof RECEIVE_PAGINATED_IDS
>(RECEIVE_PAGINATED_IDS)
export const setFirstPageCount = createAction<number, typeof SET_FIRST_PAGE_COUNT>(
  SET_FIRST_PAGE_COUNT
)
export const setFirstPageAdditionalCount = createAction<
  number,
  typeof SET_FIRST_PAGE_ADDITIONAL_COUNT
>(SET_FIRST_PAGE_ADDITIONAL_COUNT)
export const setFirstPageOffset = createAction<number | null, typeof SET_FIRST_PAGE_OFFSET>(
  SET_FIRST_PAGE_OFFSET
)
export const setIsCategoriesLoading = createAction(SET_IS_CATEGORIES_LOADING)
export const setHoodVerticals = createAction(SET_HOOD_VERTICALS)
export const setIsNavbarExpanded = createAction(SET_IS_NAVBAR_EXPANDED)

export const setLastClickedEntity = createAction<
  MaybeEmpty<Listable | Property | Pin<RawPinInventorySearch>> | null | undefined,
  typeof SET_LAST_CLICKED_ENTITY
>(SET_LAST_CLICKED_ENTITY)

/**
 * An Action used to set the route pathname for the current router location.
 * NOTE: This action is only meant to be used in `CurrentSearchView/loadData`!!!!
 */
export const setCurrentSearchRoutePathname = createAction<string, typeof SET_ROUTE_PATHNAME>(
  SET_ROUTE_PATHNAME
)
/**
 * An Action used to set the route search string for the current router search.
 * NOTE: This action is only meant to be used in `CurrentSearchView/loadData`!!!!
 */
export const setCurrentSearchRouteSearchString = createAction<
  string,
  typeof SET_ROUTE_SEARCHSTRING
>(SET_ROUTE_SEARCHSTRING)

export const errorFetchingCityGuideCityData = createAction<
  string,
  typeof ERROR_FETCHING_CITY_GUIDE_CITY_DATA
>(ERROR_FETCHING_CITY_GUIDE_CITY_DATA)
export const receiveCityGuideCityData = createAction<
  CityGuideCity,
  typeof RECEIVE_CITY_GUIDE_CITY_DATA
>(RECEIVE_CITY_GUIDE_CITY_DATA)

export const startFetchingCityGuideCityData = createAction<
  string,
  typeof START_FETCHING_CITY_GUIDE_CITY_DATA
>(START_FETCHING_CITY_GUIDE_CITY_DATA)
export const clearCityGuideCityGuideData = createAction(CLEAR_CITY_GUIDE_CITY_DATA)

export const errorFetchingCityGuideNeighborhoodData = createAction<
  string,
  typeof ERROR_FETCHING_CITY_GUIDE_NEIGHBORHOOD_DATA
>(ERROR_FETCHING_CITY_GUIDE_NEIGHBORHOOD_DATA)

export const receiveCityGuideNeighborhoodData = createAction<
  CityGuideNeighborhood,
  typeof RECEIVE_CITY_GUIDE_NEIGHBORHOOD_DATA
>(RECEIVE_CITY_GUIDE_NEIGHBORHOOD_DATA)

export const startFetchingCityGuideNeighborhoodData = createAction<
  string,
  typeof START_FETCHING_CITY_GUIDE_NEIGHBORHOOD_DATA
>(START_FETCHING_CITY_GUIDE_NEIGHBORHOOD_DATA)
export const clearCityGuideNeighborhoodData = createAction(CLEAR_CITY_GUIDE_NEIGHBORHOOD_DATA)
